/**
 * Admin-only "System knowledge" view: system_lessons mined from tool calls
 * (or seeded from the docs), which take effect for every organisation only
 * after a msys-ai-admin member approves them. Rendered by the panel-pane
 * registry through `views/systemKnowledgePane.tsx` (`agentPanelPanes`), which
 * enables `useAgentSystemKnowledge` only while the backend's
 * `agentSystemKnowledgeAccess` reports the user as an admin.
 */
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { CollapseSection, Tabs, useFormatting } from "@msys/ui";
import { useTranslate } from "@tolgee/react";
import { ApolloError, useApolloClient } from "@apollo/client";
import { getDataOrNull } from "@msys/common";
import React from "react";
import {
  canConfirmDecision,
  DECISION_ACTION_TARGET_STATUS,
  isAutomaticDecision,
  isLessonEditable,
  isReasonRequiredForAction,
  latestDecision,
  lessonsForTab,
  occurrencesSinceApproval,
  runSummaryParts,
  sortDecisionsNewestFirst,
  tabCounts,
  type SystemLesson,
  type SystemLessonDecision,
  type SystemLessonDecisionAction,
  type SystemLessonEvidence,
  type SystemLessonKind,
  type SystemLessonRun,
  type SystemLessonSource,
  type SystemLessonDecisionStatus,
  type SystemLessonStatus,
  type SystemLessonTab,
} from "./agentSystemLessons.js";
import {
  AgentSystemLessonsDocument,
  MineAgentSystemLessonsDocument,
  UpdateAgentSystemLessonDocument,
  type AgentSystemKnowledge__AgentSystemLessonFragment,
  type AgentSystemLessonsQuery,
  type AgentSystemLessonsQueryVariables,
  type MineAgentSystemLessonsMutation,
  type MineAgentSystemLessonsMutationVariables,
  type UpdateAgentSystemLessonMutation,
  type UpdateAgentSystemLessonMutationVariables,
} from "./AgentSystemKnowledge.generated.js";

const POLL_INTERVAL_MS = 5000;
const POLL_MAX_MS = 5 * 60 * 1000;

/**
 * Stable `extensions.code` values the backend's `Agent_*Error` classes set
 * (`AGENT_ERROR_CODES`, `apps/backend/src/lib/agent/agentGraphqlErrors.ts`)
 * — mirrored here as literals since the frontend cannot import backend
 * modules. Branch on these, never on message text: a message can be
 * reworded without notice, and (for `run-conflict`) the former message-only
 * signal (`isAgentSystemLessonMiningConflict`) could not distinguish a
 * mining conflict from e.g. a missing OpenRouter key without matching exact
 * strings.
 */
const AGENT_CONFLICT_CODE = "AGENT_CONFLICT";
const AGENT_INVALID_INPUT_CODE = "AGENT_INVALID_INPUT";

/** The thrown value's GraphQL `extensions.code`, or `undefined` if it has none. */
function agentErrorCode(err: unknown): string | undefined {
  if (!(err instanceof ApolloError)) return undefined;
  const code = err.graphQLErrors[0]?.extensions?.["code"];
  return typeof code === "string" ? code : undefined;
}

type SystemLessonsResponse = {
  lessons: SystemLesson[];
  lastRun: SystemLessonRun | null;
};

type SystemKnowledgeErrorKind = "load" | "run" | "run-conflict" | "save";

/** Result of a save/decide call: lets the decision dialog show a 422 inline. */
export type SaveLessonResult =
  | { ok: true }
  | { ok: false; reason: "invalid-reason" | "other" };

type SaveLessonPatch = {
  lesson?: string;
  status?: SystemLessonDecisionStatus;
  reason?: string;
};

/** One `agentSystemLessons`/`updateAgentSystemLesson` fragment node as this view's own `SystemLesson`. */
function toSystemLesson(
  node: AgentSystemKnowledge__AgentSystemLessonFragment
): SystemLesson {
  return {
    id: node.id,
    kind: node.kind,
    lesson: node.lesson,
    tools: node.tools,
    status: node.status,
    source: node.source,
    evidence: node.evidence.map(evidence => ({
      at: evidence.at,
      tools: evidence.tools,
      error: evidence.error ?? null,
      recovered: evidence.recovered,
      note: evidence.note,
    })),
    occurrences: node.occurrences,
    organisationCount: node.organisationCount,
    occurrencesSinceApproval: node.occurrencesSinceApproval,
    proposedAt: node.proposedAt,
    decidedAt: node.decidedAt ?? null,
    decidedBy: node.decidedBy ?? null,
    decisions: node.decisions.map(decision => ({
      at: decision.at,
      from: decision.from,
      to: decision.to,
      byUserId: decision.byUserId ?? null,
      byName: decision.byName,
      reason: decision.reason,
    })),
    decidedByName: node.decidedByName ?? null,
  };
}

type AgentSystemLessonsResultNode = Extract<
  AgentSystemLessonsQuery["agentSystemLessons"],
  { __typename: "AgentSystemLessonsResult" }
>;
type AgentSystemLessonRunNode = NonNullable<
  AgentSystemLessonsResultNode["lastRun"]
>;

/** One `agentSystemLessons` run node as this view's own `SystemLessonRun`. */
function toSystemLessonRun(run: AgentSystemLessonRunNode): SystemLessonRun {
  return {
    id: run.id,
    trigger: run.trigger,
    startedAt: run.startedAt,
    finishedAt: run.finishedAt ?? null,
    report: run.report
      ? {
          scannedMessages: run.report.scannedMessages,
          signals: run.report.signals,
          proposed: run.report.proposed,
          updated: run.report.updated,
          errors: run.report.errors,
        }
      : null,
  };
}

/**
 * Fetches and mutates the admin's system lessons. Kept at the panel level
 * (like useAgentChangelog) so the header button's dot badge reflects open
 * proposals even before the view itself is opened. Moved off `GET`/`PATCH
 * /api/agent/admin/system-lessons` and `POST /api/agent/admin/
 * mine-system-lessons` onto `agentSystemLessons`/`updateAgentSystemLesson`/
 * `mineAgentSystemLessons` (MEIS-13520, P5 GraphQL port).
 *
 * `resetKey` is the caller's identity (`useAgentIdentityKey()`): when it
 * changes, the state is cleared and refetched, and every response or poll
 * started for the previous identity is dropped (`generationRef`), so one
 * user's lessons never end up in another user's state.
 */
export function useAgentSystemKnowledge(enabled: boolean, resetKey: string) {
  const apolloClient = useApolloClient();
  const [lessons, setLessons] = React.useState<SystemLesson[]>([]);
  const [lastRun, setLastRun] = React.useState<SystemLessonRun | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [errorKind, setErrorKind] =
    React.useState<SystemKnowledgeErrorKind | null>(null);
  const [running, setRunning] = React.useState(false);
  const [savingId, setSavingId] = React.useState<string | null>(null);
  const cancelledRef = React.useRef(false);
  const generationRef = React.useRef(0);
  const isCurrent = React.useCallback(
    (generation: number) =>
      !cancelledRef.current && generationRef.current === generation,
    []
  );

  React.useEffect(() => {
    cancelledRef.current = false;
    return () => {
      cancelledRef.current = true;
    };
  }, []);

  const fetchLessons =
    React.useCallback(async (): Promise<SystemLessonsResponse> => {
      const generation = generationRef.current;
      const { data } = await apolloClient.query<
        AgentSystemLessonsQuery,
        AgentSystemLessonsQueryVariables
      >({
        query: AgentSystemLessonsDocument,
        fetchPolicy: "network-only",
        context: { noErrorNotification: true },
      });
      const result = getDataOrNull(data.agentSystemLessons);
      if (!result) throw new Error("Missing permissions or capabilities");
      const response: SystemLessonsResponse = {
        lessons: result.lessons.map(toSystemLesson),
        lastRun: result.lastRun ? toSystemLessonRun(result.lastRun) : null,
      };
      if (isCurrent(generation)) {
        setLessons(response.lessons);
        setLastRun(response.lastRun);
      }
      return response;
    }, [apolloClient, isCurrent]);

  const refresh = React.useCallback(async () => {
    const generation = generationRef.current;
    setLoading(true);
    setErrorKind(null);
    try {
      await fetchLessons();
    } catch {
      if (isCurrent(generation)) setErrorKind("load");
    } finally {
      if (isCurrent(generation)) setLoading(false);
    }
  }, [fetchLessons, isCurrent]);

  React.useEffect(() => {
    generationRef.current += 1;
    setLessons([]);
    setLastRun(null);
    setErrorKind(null);
    setLoading(false);
    setRunning(false);
    setSavingId(null);
    if (!enabled) return;
    void refresh();
    // Fetch once on becoming enabled or on an identity change; further
    // updates come from the view's own actions (save/decide/run), not from
    // re-running this effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, resetKey]);

  const runNow = React.useCallback(async () => {
    const generation = generationRef.current;
    setErrorKind(null);
    try {
      await apolloClient.mutate<
        MineAgentSystemLessonsMutation,
        MineAgentSystemLessonsMutationVariables
      >({
        mutation: MineAgentSystemLessonsDocument,
        context: { noErrorNotification: true },
      });
    } catch (err) {
      if (isCurrent(generation))
        setErrorKind(
          agentErrorCode(err) === AGENT_CONFLICT_CODE ? "run-conflict" : "run"
        );
      return;
    }
    if (!isCurrent(generation)) return;
    setRunning(true);
    try {
      const deadline = Date.now() + POLL_MAX_MS;
      while (isCurrent(generation) && Date.now() < deadline) {
        await new Promise(resolve => setTimeout(resolve, POLL_INTERVAL_MS));
        if (!isCurrent(generation)) break;
        const data = await fetchLessons();
        if (data.lastRun?.finishedAt) break;
      }
    } catch {
      if (isCurrent(generation)) setErrorKind("run");
    } finally {
      if (isCurrent(generation)) setRunning(false);
    }
  }, [apolloClient, fetchLessons, isCurrent]);

  const saveLesson = React.useCallback(
    async (id: string, patch: SaveLessonPatch): Promise<SaveLessonResult> => {
      const generation = generationRef.current;
      setSavingId(id);
      try {
        const { data } = await apolloClient.mutate<
          UpdateAgentSystemLessonMutation,
          UpdateAgentSystemLessonMutationVariables
        >({
          mutation: UpdateAgentSystemLessonDocument,
          variables: {
            id,
            ...(patch.lesson !== undefined ? { lesson: patch.lesson } : {}),
            ...(patch.status !== undefined ? { status: patch.status } : {}),
            ...(patch.reason !== undefined ? { reason: patch.reason } : {}),
          },
          context: { noErrorNotification: true },
        });
        if (!data) throw new Error("The update did not return a result.");
        const lesson = toSystemLesson(data.updateAgentSystemLesson.lesson);
        if (isCurrent(generation)) {
          setLessons(list => list.map(l => (l.id === id ? lesson : l)));
        }
        return { ok: true };
      } catch (err) {
        const invalidReason = agentErrorCode(err) === AGENT_INVALID_INPUT_CODE;
        // A missing reason is shown inline in the decision dialog by the
        // caller instead of the panel-wide alert.
        if (!invalidReason && isCurrent(generation)) setErrorKind("save");
        return {
          ok: false,
          reason: invalidReason ? "invalid-reason" : "other",
        };
      } finally {
        if (isCurrent(generation)) setSavingId(null);
      }
    },
    [apolloClient, isCurrent]
  );

  const dismissError = React.useCallback(() => setErrorKind(null), []);

  return {
    lessons,
    lastRun,
    loading,
    errorKind,
    running,
    savingId,
    runNow,
    saveLesson,
    dismissError,
  };
}

export type AgentSystemKnowledgeState = ReturnType<
  typeof useAgentSystemKnowledge
>;

/** The four decision buttons' labels; also the dialog's title and confirm button. */
function useDecisionActionTitle() {
  const { t } = useTranslate(["Assistant", "Integrations"]);
  return React.useCallback(
    (action: SystemLessonDecisionAction): string => {
      switch (action) {
        case "approve":
          return t("Approve", { ns: "Assistant" });
        case "reapprove":
          return t("Approve again", { ns: "Assistant" });
        case "reject":
          return t("Reject", { ns: "Integrations" });
        case "retire":
          return t("Retire", { ns: "Assistant" });
      }
    },
    [t]
  );
}

function EvidenceItem({ evidence }: { evidence: SystemLessonEvidence }) {
  const { t } = useTranslate(["Assistant", "Integrations"]);
  const { getFormattedDateTime } = useFormatting();
  return (
    <Box sx={{ borderLeft: 2, borderColor: "divider", pl: 1 }}>
      <Stack
        direction="row"
        spacing={0.5}
        sx={{ alignItems: "center", flexWrap: "wrap", rowGap: 0.5 }}
      >
        <Typography variant="caption" color="text.secondary">
          {getFormattedDateTime(evidence.at)}
        </Typography>
        {evidence.tools.map(tool => (
          <Chip key={tool} size="small" variant="outlined" label={tool} />
        ))}
      </Stack>
      {evidence.note && (
        <Typography variant="body2" component="div">
          {evidence.note}
        </Typography>
      )}
      {evidence.error && (
        <Box sx={{ mt: 0.5 }}>
          <Typography
            variant="caption"
            component="div"
            sx={{ fontWeight: 600 }}
          >
            {t("Failed", { ns: "Integrations" })}
          </Typography>
          <Box
            component="pre"
            sx={{
              m: 0,
              mt: 0.25,
              p: 0.5,
              fontFamily: "monospace",
              fontSize: 11,
              bgcolor: "action.hover",
              borderRadius: 0.5,
              overflowX: "auto",
              maxHeight: 120,
              overflowY: "auto",
            }}
          >
            {evidence.error}
          </Box>
        </Box>
      )}
      {evidence.recovered && (
        <Typography variant="caption" color="text.secondary" component="div">
          {t("Then succeeded on retry", { ns: "Assistant" })}
        </Typography>
      )}
    </Box>
  );
}

/** The status a decision led to or (before the first decision) is still in. */
function useStatusLabel() {
  const { t } = useTranslate(["Assistant", "Global", "Integrations"]);
  return React.useCallback(
    (status: SystemLessonStatus): string => {
      switch (status) {
        case "proposed":
          return t("Open", { ns: "Global" });
        case "approved":
          return t("Approved", { ns: "Integrations" });
        case "rejected":
          return t("Rejected", { ns: "Integrations" });
        case "retired":
          return t("Retired", { ns: "Assistant" });
      }
    },
    [t]
  );
}

/**
 * One decision's summary line, e.g. "Approved by Matthias Welk on
 * 26.09.2026, 04:10: too specific". Also used for the latest decision at the
 * top of a card and for each entry in the "History" list.
 */
function DecisionLine({ decision }: { decision: SystemLessonDecision | null }) {
  const { t } = useTranslate(["Assistant"]);
  const { getFormattedDateTime } = useFormatting();
  const statusLabel = useStatusLabel();
  if (!decision) return null;

  const name = isAutomaticDecision(decision)
    ? t("Automatic analysis", { ns: "Assistant" })
    : decision.byName;
  const status = statusLabel(decision.to);
  const date = getFormattedDateTime(decision.at);
  const line = name
    ? t("{status} by {name} on {date}", { ns: "Assistant", status, name, date })
    : t("{status} on {date}", { ns: "Assistant", status, date });

  return (
    <Typography variant="caption" color="text.secondary" component="div">
      {line}
      {decision.reason ? `: ${decision.reason}` : ""}
    </Typography>
  );
}

function DecisionDialog({
  action,
  saving,
  onCancel,
  onConfirm,
}: {
  action: SystemLessonDecisionAction;
  saving: boolean;
  onCancel: () => void;
  onConfirm: (reason: string) => Promise<SaveLessonResult>;
}) {
  const { t } = useTranslate(["Assistant", "Global", "Templates"]);
  const decisionActionTitle = useDecisionActionTitle();
  const [reason, setReason] = React.useState("");
  const [error, setError] = React.useState<string | null>(null);
  const title = decisionActionTitle(action);
  const required = isReasonRequiredForAction(action);
  const showApprovalHint = action === "approve" || action === "reapprove";

  const confirm = async () => {
    setError(null);
    const result = await onConfirm(reason.trim());
    if (!result.ok) {
      setError(
        result.reason === "invalid-reason"
          ? t("A reason is required", { ns: "Assistant" })
          : t("Could not save the decision", { ns: "Assistant" })
      );
    }
  };

  return (
    <Dialog open onClose={saving ? undefined : onCancel}>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Stack spacing={1} sx={{ pt: 0.5, minWidth: 320 }}>
          {showApprovalHint && (
            <Alert severity="info" sx={{ py: 0 }}>
              {t(
                "Approved rules go into every organisation's prompt. Make sure the wording contains no organisation data (names, prices, texts).",
                { ns: "Assistant" }
              )}
            </Alert>
          )}
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={2}
            label={t("Reason", { ns: "Templates" })}
            required={required}
            value={reason}
            disabled={saving}
            slotProps={{ htmlInput: { maxLength: 1000 } }}
            onChange={event => setReason(event.target.value)}
          />
          {error && (
            <Alert severity="error" sx={{ py: 0 }}>
              {error}
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={saving}>
          {t("Cancel", { ns: "Global" })}
        </Button>
        <Button
          variant="contained"
          disabled={saving || !canConfirmDecision(action, reason)}
          onClick={() => void confirm()}
        >
          {title}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function useKindLabel() {
  const { t } = useTranslate(["Assistant"]);
  return React.useCallback(
    (kind: SystemLessonKind): string =>
      kind === "tool_usage"
        ? t("Tool usage", { ns: "Assistant" })
        : t("System concept", { ns: "Assistant" }),
    [t]
  );
}

function useSourceLabel() {
  const { t } = useTranslate(["Assistant"]);
  return React.useCallback(
    (source: SystemLessonSource): string =>
      source === "mined"
        ? t("From chats", { ns: "Assistant" })
        : t("From docs", { ns: "Assistant" }),
    [t]
  );
}

function LessonCard({
  lesson,
  saving,
  onSave,
}: {
  lesson: SystemLesson;
  saving: boolean;
  onSave: (patch: SaveLessonPatch) => Promise<SaveLessonResult>;
}) {
  const { t } = useTranslate(["Assistant", "Global", "Integrations"]);
  const kindLabel = useKindLabel();
  const sourceLabel = useSourceLabel();
  const decisionActionTitle = useDecisionActionTitle();
  const editable = isLessonEditable(lesson.status);
  const [editing, setEditing] = React.useState(false);
  const [text, setText] = React.useState(lesson.lesson);
  React.useEffect(() => {
    setText(lesson.lesson);
    setEditing(false);
  }, [lesson.id, lesson.lesson]);
  const [pendingAction, setPendingAction] =
    React.useState<SystemLessonDecisionAction | null>(null);

  const confirmDecision = async (reason: string): Promise<SaveLessonResult> => {
    if (!pendingAction) return { ok: true };
    const patch: SaveLessonPatch = {
      status: DECISION_ACTION_TARGET_STATUS[pendingAction],
    };
    if (reason !== "") patch.reason = reason;
    const result = await onSave(patch);
    if (result.ok) setPendingAction(null);
    return result;
  };

  const saveWording = async () => {
    const trimmed = text.trim();
    if (trimmed === "" || trimmed === lesson.lesson) {
      setText(lesson.lesson);
      setEditing(false);
      return;
    }
    const result = await onSave({ lesson: trimmed });
    if (result.ok) setEditing(false);
  };

  const sinceApproval = occurrencesSinceApproval(lesson);
  const latest = latestDecision(lesson);
  const history = sortDecisionsNewestFirst(lesson.decisions);

  return (
    <Paper variant="outlined" sx={{ p: 1 }}>
      <Stack spacing={0.75}>
        <Stack
          direction="row"
          spacing={0.5}
          sx={{ flexWrap: "wrap", rowGap: 0.5, alignItems: "center" }}
        >
          <Chip size="small" color="primary" label={kindLabel(lesson.kind)} />
          {lesson.tools.map(tool => (
            <Chip key={tool} size="small" variant="outlined" label={tool} />
          ))}
          <Chip
            size="small"
            variant="outlined"
            label={sourceLabel(lesson.source)}
          />
        </Stack>

        {editing ? (
          <Stack spacing={0.5}>
            <TextField
              multiline
              size="small"
              minRows={2}
              fullWidth
              value={text}
              disabled={saving}
              onChange={event => setText(event.target.value)}
            />
            <Stack direction="row" spacing={1}>
              <Button
                size="small"
                variant="contained"
                disabled={saving}
                onClick={() => void saveWording()}
              >
                {t("Save", { ns: "Global" })}
              </Button>
              <Button
                size="small"
                disabled={saving}
                onClick={() => {
                  setText(lesson.lesson);
                  setEditing(false);
                }}
              >
                {t("Cancel", { ns: "Global" })}
              </Button>
            </Stack>
          </Stack>
        ) : (
          <Stack direction="row" spacing={1} sx={{ alignItems: "flex-start" }}>
            <Typography variant="body2" sx={{ flex: 1 }}>
              {lesson.lesson}
            </Typography>
            {editable && (
              <Button size="small" onClick={() => setEditing(true)}>
                {t("Edit", { ns: "Global" })}
              </Button>
            )}
          </Stack>
        )}

        <Typography variant="caption" color="text.secondary">
          {t(
            "Seen {occurrences}× in {organisations, plural, one {# organisation} other {# organisations}}",
            {
              ns: "Assistant",
              occurrences: lesson.occurrences,
              organisations: lesson.organisationCount,
            }
          )}
          {sinceApproval !== null
            ? ` · ${t("{count}× since approval", { ns: "Assistant", count: sinceApproval })}`
            : ""}
        </Typography>

        <DecisionLine decision={latest} />

        {lesson.evidence.length > 0 && (
          <CollapseSection
            title={t("Evidence", { ns: "Assistant" })}
            itemCount={lesson.evidence.length}
          >
            <Stack spacing={1}>
              {lesson.evidence.map((evidence, index) => (
                <EvidenceItem key={index} evidence={evidence} />
              ))}
            </Stack>
          </CollapseSection>
        )}

        {history.length > 0 && (
          <CollapseSection
            title={t("History", { ns: "Integrations" })}
            itemCount={history.length}
          >
            <Stack spacing={1}>
              {history.map((decision, index) => (
                <Box
                  key={index}
                  sx={{ borderLeft: 2, borderColor: "divider", pl: 1 }}
                >
                  <DecisionLine decision={decision} />
                </Box>
              ))}
            </Stack>
          </CollapseSection>
        )}

        <Stack
          direction="row"
          spacing={1}
          sx={{ flexWrap: "wrap", rowGap: 0.5 }}
        >
          {lesson.status === "proposed" && (
            <>
              <Button
                size="small"
                variant="contained"
                disabled={saving}
                onClick={() => setPendingAction("approve")}
              >
                {decisionActionTitle("approve")}
              </Button>
              <Button
                size="small"
                color="error"
                disabled={saving}
                onClick={() => setPendingAction("reject")}
              >
                {decisionActionTitle("reject")}
              </Button>
            </>
          )}
          {lesson.status === "approved" && (
            <Button
              size="small"
              color="warning"
              disabled={saving}
              onClick={() => setPendingAction("retire")}
            >
              {decisionActionTitle("retire")}
            </Button>
          )}
          {(lesson.status === "rejected" || lesson.status === "retired") && (
            <Button
              size="small"
              disabled={saving}
              onClick={() => setPendingAction("reapprove")}
            >
              {decisionActionTitle("reapprove")}
            </Button>
          )}
        </Stack>
      </Stack>

      {pendingAction && (
        <DecisionDialog
          action={pendingAction}
          saving={saving}
          onCancel={() => setPendingAction(null)}
          onConfirm={confirmDecision}
        />
      )}
    </Paper>
  );
}

export function AgentSystemKnowledge({
  state,
}: {
  state: AgentSystemKnowledgeState;
}) {
  const { t } = useTranslate(["Assistant", "Global", "OrganisationSettings"]);
  const { getFormattedDateTime } = useFormatting();
  const {
    lessons,
    lastRun,
    loading,
    errorKind,
    running,
    savingId,
    runNow,
    saveLesson,
    dismissError,
  } = state;
  const [tab, setTab] = React.useState<SystemLessonTab>("proposed");
  const counts = tabCounts(lessons);
  const shown = lessonsForTab(lessons, tab);
  const summary = runSummaryParts(lastRun);

  const tabLabels: Record<SystemLessonTab, string> = {
    proposed: t("Open", { ns: "Global" }),
    approved: t("Active", { ns: "OrganisationSettings" }),
    rejected: t("Rejected & retired", { ns: "Assistant" }),
  };
  const emptyText: Record<SystemLessonTab, string> = {
    proposed: t("No open proposals.", { ns: "Assistant" }),
    approved: t("No active rules.", { ns: "Assistant" }),
    rejected: t("No rejected or retired rules.", { ns: "Assistant" }),
  };
  const errorLabels: Record<SystemKnowledgeErrorKind, string> = {
    load: t("Could not load the system knowledge", { ns: "Assistant" }),
    run: t("Could not load the system knowledge", { ns: "Assistant" }),
    "run-conflict": t("An analysis is already running", { ns: "Assistant" }),
    save: t("Could not save the decision", { ns: "Assistant" }),
  };

  return (
    <Stack spacing={1.5}>
      <Typography variant="subtitle2">
        {t("System knowledge (admin)", { ns: "Assistant" })}
      </Typography>

      <Stack spacing={0.5}>
        <Typography variant="caption" color="text.secondary">
          {summary
            ? t(
                "Last analysis: {date} – {scanned} messages scanned, {proposed} new proposals",
                {
                  ns: "Assistant",
                  date: getFormattedDateTime(summary.finishedAt),
                  scanned: summary.scannedMessages,
                  proposed: summary.newProposals,
                }
              )
            : t("No analysis has run yet.", { ns: "Assistant" })}
        </Typography>
        {summary && summary.errors.length > 0 && (
          <Alert severity="warning" sx={{ py: 0 }}>
            {t("{count} errors", {
              ns: "Assistant",
              count: summary.errors.length,
            })}
          </Alert>
        )}
        <Box>
          <Button
            size="small"
            variant="outlined"
            disabled={running}
            startIcon={running ? <CircularProgress size={14} /> : undefined}
            onClick={() => void runNow()}
          >
            {running
              ? t("Running…", { ns: "Assistant" })
              : t("Analyse now", { ns: "Assistant" })}
          </Button>
        </Box>
      </Stack>

      {errorKind && (
        <Alert severity="error" onClose={dismissError}>
          {errorLabels[errorKind]}
        </Alert>
      )}

      <Tabs
        condensed
        value={tab}
        onChange={setTab}
        options={[
          {
            value: "proposed",
            label: tabLabels.proposed,
            count: counts.proposed,
          },
          {
            value: "approved",
            label: tabLabels.approved,
            count: counts.approved,
          },
          {
            value: "rejected",
            label: tabLabels.rejected,
            count: counts.rejected,
          },
        ]}
      />

      {loading && lessons.length === 0 && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
          <CircularProgress size={20} />
        </Box>
      )}

      {!loading && shown.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          {emptyText[tab]}
        </Typography>
      )}

      <Stack spacing={1}>
        {shown.map(lesson => (
          <LessonCard
            key={lesson.id}
            lesson={lesson}
            saving={savingId === lesson.id}
            onSave={patch => saveLesson(lesson.id, patch)}
          />
        ))}
      </Stack>
    </Stack>
  );
}
